<template>
  <div
    ref="container"
    class="rail-list-ellipsis"
  >
    <v-tooltip
      :key="`${keyPrefix}-${itemKey}-${epoch}`"
      bottom
      content-class="rail-list-ellipsis__tooltip"
    >
      <template slot="activator" slot-scope="{ on, attrs }">
        <span
          ref="label"
          v-bind="attrs"
          v-on="on"
          :class="labelClass"
          :style="mergedStyle"
        >
          {{ renderedText }}
        </span>
      </template>
      <span>{{ text }}</span>
    </v-tooltip>
  </div>
</template>

<script>
  import {
    createTextWidthMeasurer,
    ellipsisString,
    ellipsisStringForWidth
  } from '@/utils/displayUtils';

  export default {
    name: 'RailListEllipsisTooltip',
    props: {
      text: { type: String, default: '' },
      itemKey: { type: [String, Number], default: '' },
      epoch: { type: Number, default: 0 },
      keyPrefix: { type: String, default: 'rail-tip' },
      textClass: { type: [String, Array, Object], default: '' },
      textStyle: { type: [Object, String], default: null },
      ellipsis: {
        type: Object,
        default: () => ({ type: 'length', frontLength: 5, backLength: 11 })
      }
    },
    data() {
      return {
        containerWidth: 0,
        measureTextWidth: null,
        resizeObserver: null,
        resizeRafId: null
      };
    },
    computed: {
      useFitWidth() {
        return this.ellipsis?.type === 'fitWidth';
      },
      useCssEllipsis() {
        return this.ellipsis?.type === 'css';
      },
      useFullWidth() {
        return this.useFitWidth || this.useCssEllipsis;
      },
      renderedText() {
        if (this.useFitWidth && this.containerWidth > 0 && this.measureTextWidth) {
          return ellipsisStringForWidth(
            this.text,
            this.containerWidth,
            this.measureTextWidth,
            this.ellipsis
          );
        }
        if (this.useFitWidth) {
          return ellipsisString(this.text, {
            type: 'length',
            frontLength: this.ellipsis?.frontLength ?? 5,
            backLength: this.ellipsis?.backLength ?? 11
          });
        }
        return ellipsisString(this.text, this.ellipsis);
      },
      labelClass() {
        const widthClass = this.useFullWidth ? ['d-block', 'w-100'] : ['d-inline-block'];
        const truncateClass = this.useCssEllipsis ? ['text-truncate'] : [];
        return [...widthClass, ...truncateClass, this.textClass];
      },
      mergedStyle() {
        return {
          maxWidth: '100%',
          verticalAlign: 'bottom',
          ...(this.useFullWidth ? { width: '100%' } : {}),
          ...(this.textStyle && typeof this.textStyle === 'object' ? this.textStyle : {})
        };
      }
    },
    watch: {
      text() {
        this.scheduleWidthSync();
      },
      ellipsis: {
        deep: true,
        handler() {
          this.scheduleWidthSync();
        }
      },
      epoch() {
        this.scheduleWidthSync();
      }
    },
    mounted() {
      this.scheduleWidthSync();
      this.attachResizeObserver();
    },
    updated() {
      if (this.useFitWidth) {
        this.scheduleWidthSync();
      }
    },
    beforeDestroy() {
      this.detachResizeObserver();
      if (this.resizeRafId) {
        cancelAnimationFrame(this.resizeRafId);
        this.resizeRafId = null;
      }
    },
    methods: {
      scheduleWidthSync() {
        this.$nextTick(() => {
          this.syncTextMeasurer();
          this.updateContainerWidth();
        });
      },
      syncTextMeasurer() {
        if (!this.useFitWidth) {
          this.measureTextWidth = null;
          return;
        }
        const el = this.$refs.label;
        if (!el) {
          return;
        }
        this.measureTextWidth = createTextWidthMeasurer(el);
      },
      readContainerWidth() {
        const container = this.$refs.container;
        if (container) {
          const width = container.clientWidth || container.offsetWidth || 0;
          if (width > 0) {
            return width;
          }
        }
        const label = this.$refs.label;
        if (!label?.parentElement) {
          return 0;
        }
        return label.parentElement.clientWidth || label.parentElement.offsetWidth || 0;
      },
      updateContainerWidth() {
        if (!this.useFitWidth) {
          return;
        }
        const nextWidth = this.readContainerWidth();
        if (nextWidth !== this.containerWidth) {
          this.containerWidth = nextWidth;
        }
      },
      queueResizeUpdate() {
        if (this.resizeRafId) {
          return;
        }
        this.resizeRafId = requestAnimationFrame(() => {
          this.resizeRafId = null;
          this.syncTextMeasurer();
          this.updateContainerWidth();
        });
      },
      attachResizeObserver() {
        this.detachResizeObserver();
        if (!this.useFitWidth || typeof ResizeObserver === 'undefined') {
          return;
        }
        const container = this.$refs.container;
        if (!container) {
          return;
        }
        this.resizeObserver = new ResizeObserver(() => {
          this.queueResizeUpdate();
        });
        this.resizeObserver.observe(container);
        const label = this.$refs.label;
        if (label) {
          this.resizeObserver.observe(label);
        }
      },
      detachResizeObserver() {
        this.resizeObserver?.disconnect?.();
        this.resizeObserver = null;
      }
    }
  };
</script>

<style scoped>
  .rail-list-ellipsis {
    display: block;
    width: 100%;
    min-width: 0;
    overflow: hidden;
  }
</style>

<style>
  /* v-tooltip root — activator가 셀 전체 width를 쓰도록 */
  .rail-list-ellipsis > .v-tooltip {
    display: block;
    width: 100%;
  }
</style>
